import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiArrowLeft, FiCalendar, FiClock, FiUsers, FiStar } from 'react-icons/fi';
import { blogService, courseService } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import Button from '../components/Button';
import { useSettings } from '../contexts/SettingsContext';

// Page shell
const Page = styled.div`
	background: ${p => p.theme.colors.gray[50]};
`;

const Container = styled.div`
	max-width: ${p => p.theme.breakpoints.wide};
	margin: 0 auto;
	padding: ${p => p.theme.spacing['2xl']} ${p => p.theme.spacing.lg};

	@media (max-width: ${p => p.theme.breakpoints.tablet}) {
		padding: ${p => p.theme.spacing.xl} ${p => p.theme.spacing.md};
	}
	@media (max-width: ${p => p.theme.breakpoints.mobile}) {
		padding: ${p => p.theme.spacing.lg} ${p => p.theme.spacing.sm};
	}
`;

// Breadcrumb/back
const Back = styled(Link)`
	display: inline-flex;
	align-items: center;
	gap: ${p => p.theme.spacing.sm};
	text-decoration: none;
	color: ${p => p.theme.colors.gray[600]};
	font-weight: ${p => p.theme.fontWeights.medium};
	margin-bottom: ${p => p.theme.spacing.lg};

	&:hover { color: ${p => p.theme.colors.dark}; }
`;

// Article layout
const Grid = styled.div`
	display: grid;
	grid-template-columns: 1fr 360px;
	gap: ${p => p.theme.spacing['3xl']};

	@media (max-width: 1200px) {
		grid-template-columns: 1fr 320px;
	}
	@media (max-width: ${p => p.theme.breakpoints.desktop}) {
		grid-template-columns: 1fr;
		gap: ${p => p.theme.spacing['2xl']};
	}
`;

const Article = styled.article`
	min-width: 0;
`;

const Title = styled.h1`
	font-size: clamp(28px, 4vw, 44px);
	line-height: 1.15;
	color: ${p => p.theme.colors.dark};
	margin: 0 0 ${p => p.theme.spacing.md};
`;

const Meta = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: ${p => p.theme.spacing.md};
	color: ${p => p.theme.colors.gray[600]};
	font-weight: ${p => p.theme.fontWeights.medium};
	margin-bottom: ${p => p.theme.spacing['2xl']};

	.pill {
		display: inline-flex;
		align-items: center;
		gap: ${p => p.theme.spacing.xs};
		background: ${p => p.theme.colors.gray[100]};
		border-radius: ${p => p.theme.borderRadius.full};
		padding: 6px 12px;
		transition: ${p => p.theme.transitions.fast};
		border: 1px solid ${p => p.theme.colors.gray[200]};

		&:hover {
			background: ${p => p.theme.colors.gray[200]};
			transform: translateY(-1px);
		}
	}

	.category {
		background: ${p => p.theme.colors.secondary};
		color: ${p => p.theme.colors.light};
		border-color: ${p => p.theme.colors.secondary};
		box-shadow: ${p => p.theme.shadows.sm};

		&:hover {
			background: ${p => p.theme.colors.secondary};
			filter: brightness(1.1);
		}
	}
`;

const AuthorRow = styled.div`
	display: flex;
	align-items: center;
	gap: ${p => p.theme.spacing.md};
	margin-bottom: ${p => p.theme.spacing['2xl']};
	padding: ${p => p.theme.spacing.lg};
	background: ${p => p.theme.colors.gray[50]};
	border-radius: ${p => p.theme.borderRadius.lg};
	border: 1px solid ${p => p.theme.colors.gray[200]};
	transition: ${p => p.theme.transitions.default};

	&:hover {
		background: ${p => p.theme.colors.gray[100]};
		transform: translateX(4px);
	}

	.avatar { 
		width: 56px; height: 56px; border-radius: 50%; 
		background: ${p => p.theme.colors.gradient.primary};
		display: grid; place-items: center; 
		font-weight: 700; 
		color: ${p => p.theme.colors.light};
		box-shadow: ${p => p.theme.shadows.md};
	}
	.info { line-height: 1.4; }
	.name { font-weight: 700; color: ${p => p.theme.colors.dark}; }
	.bio { color: ${p => p.theme.colors.gray[600]}; font-size: ${p => p.theme.fontSizes.sm}; }
`;

const FeaturedImage = styled.img`
	width: 100%;
	height: auto;
	border-radius: ${p => p.theme.borderRadius.xl};
	margin: 0 0 ${p => p.theme.spacing['2xl']};
	box-shadow: 0 12px 32px rgba(0,0,0,0.12);
	border: 1px solid ${p => p.theme.colors.gray[200]};
	transition: ${p => p.theme.transitions.default};

	&:hover {
		box-shadow: 0 16px 40px rgba(0,0,0,0.16);
		transform: scale(1.01);
	}
`;

const Content = styled.div`
	color: ${p => p.theme.colors.gray[800]};
	font-size: clamp(${p => p.theme.fontSizes.base}, 1.2vw, ${p => p.theme.fontSizes.xl});
	line-height: 1.9;

		h2, h3 { color: ${p => p.theme.colors.dark}; margin: ${p => p.theme.spacing['2xl']} 0 ${p => p.theme.spacing.md}; }
		h2 { font-size: clamp(${p => p.theme.fontSizes.xl}, 2.2vw, ${p => p.theme.fontSizes['3xl']}); }
		h3 { font-size: clamp(${p => p.theme.fontSizes.lg}, 1.8vw, ${p => p.theme.fontSizes['2xl']}); }
		p { margin: 0 0 ${p => p.theme.spacing.md}; }
		ul, ol { margin: ${p => p.theme.spacing.md} 0 ${p => p.theme.spacing.xl}; padding-left: ${p => p.theme.spacing['2xl']}; }
	img { max-width: 100%; height: auto; border-radius: ${p => p.theme.borderRadius.lg}; box-shadow: 0 4px 12px rgba(0,0,0,0.06); margin: ${p => p.theme.spacing['2xl']} 0; }
	blockquote { border-left: 4px solid ${p => p.theme.colors.primary}; background: rgba(255,68,68,0.04); padding: ${p => p.theme.spacing.lg} ${p => p.theme.spacing['2xl']}; border-radius: ${p => p.theme.borderRadius.md}; font-style: italic; }
`;

const Tags = styled.div`
	margin-top: ${p => p.theme.spacing['3xl']};
	padding-top: ${p => p.theme.spacing['2xl']};
	border-top: 2px solid ${p => p.theme.colors.gray[200]};
	display: flex; 
	flex-wrap: wrap; 
	gap: ${p => p.theme.spacing.sm};

	.tag { 
		background: ${p => p.theme.colors.gray[100]}; 
		color: ${p => p.theme.colors.gray[800]}; 
		border-radius: 999px; 
		padding: 8px 16px; 
		font-weight: 600;
		border: 1px solid ${p => p.theme.colors.gray[300]};
		transition: ${p => p.theme.transitions.fast};
		cursor: pointer;

		&:hover {
			background: ${p => p.theme.colors.primary};
			color: ${p => p.theme.colors.light};
			border-color: ${p => p.theme.colors.primary};
			transform: translateY(-2px);
			box-shadow: ${p => p.theme.shadows.sm};
		}
	}
`;

// Sidebar
const Aside = styled.aside`
	position: sticky;
	top: 96px;
	align-self: start;

	@media (max-width: ${p => p.theme.breakpoints.desktop}) {
		position: static;
		top: auto;
	}
`;

const Card = styled.div`
	background: ${p => p.theme.colors.light};
	border-radius: ${p => p.theme.borderRadius.xl};
	padding: ${p => p.theme.spacing.xl};
	box-shadow: 0 8px 24px rgba(0,0,0,0.1);
	border: 1px solid ${p => p.theme.colors.gray[200]};
	margin-bottom: ${p => p.theme.spacing.xl};
	position: relative;
	overflow: hidden;
	transition: ${p => p.theme.transitions.default};

	&::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		height: 4px;
		background: ${p => p.theme.colors.gradient.primary};
	}

	&:hover {
		box-shadow: 0 12px 32px rgba(0,0,0,0.15);
		transform: translateY(-2px);
	}
`;

const Toc = styled.ul`
	list-style: none; padding: 0; margin: 0;
	li { margin-bottom: 8px; }
	a { color: ${p => p.theme.colors.gray[700]}; text-decoration: none; font-size: ${p => p.theme.fontSizes.sm}; }
	.lvl-2 { padding-left: ${p => p.theme.spacing.md}; }
	.lvl-3 { padding-left: ${p => p.theme.spacing.lg}; }
`;

const CourseMini = styled(Link)`
	display: block; text-decoration: none;
	background: ${p => p.theme.colors.gray[50]};
	border-radius: ${p => p.theme.borderRadius.lg};
	padding: ${p => p.theme.spacing.md};
	margin-bottom: ${p => p.theme.spacing.md};
	border: 1px solid ${p => p.theme.colors.gray[200]};
	color: ${p => p.theme.colors.dark};

	.title { font-weight: 700; font-size: ${p => p.theme.fontSizes.base}; margin-bottom: 6px; }
	.meta { display: flex; gap: ${p => p.theme.spacing.md}; color: ${p => p.theme.colors.gray[600]}; font-size: ${p => p.theme.fontSizes.xs}; }
`;

const Progress = styled.div`
	position: fixed; top: 64px; left: 0; right: 0; height: 3px; z-index: 10;
	background: transparent;
`;

const ProgressFill = styled.div`
	height: 100%; background: ${p => p.theme.colors.primary}; width: ${p => p.$w || 0}%; transition: width .12s ease-out;
`;

export default function BlogDetail() {
	const { slug } = useParams();
	const { settings } = useSettings();
	const [loading, setLoading] = useState(true);
	const [post, setPost] = useState(null);
	const [progress, setProgress] = useState(0);
	const [featuredCourses, setFeaturedCourses] = useState([]);

	useEffect(() => {
		const load = async () => {
			setLoading(true);
			try {
				const data = await blogService.getBlogBySlug(slug);
				setPost(data || null);
			} catch (e) {
				// eslint-disable-next-line no-console
				setPost(null);
			} finally { setLoading(false); }
		};
		load();
	}, [slug]);

	useEffect(() => {
		const load = async () => {
			try {
				const courses = await courseService.getFeaturedCourses();
				setFeaturedCourses(Array.isArray(courses) ? courses.slice(0,3) : []);
			} catch { setFeaturedCourses([]); }
		};
		load();
	}, []);

	useEffect(() => {
		const onScroll = () => {
			const doc = document.documentElement;
			const s = doc.scrollTop || document.body.scrollTop;
			const m = doc.scrollHeight - doc.clientHeight;
			setProgress(m > 0 ? (s / m) * 100 : 0);
		};
		window.addEventListener('scroll', onScroll, { passive: true });
		onScroll();
		return () => window.removeEventListener('scroll', onScroll);
	}, []);

	const toc = useMemo(() => {
			if (!post?.content) return [];
			const isHtml = /<\/?[a-z][\s\S]*>/i.test(post.content);
			if (isHtml) {
				const toc = [];
				const re = /<h([1-3])[^>]*>([\s\S]*?)<\/h\1>/gi;
				let m;
				while ((m = re.exec(post.content)) !== null) {
					const level = parseInt(m[1], 10);
					const raw = m[2].replace(/<[^>]+>/g, '');
					const id = raw.toLowerCase().replace(/[^a-z0-9\s-]/g, '').trim().replace(/\s+/g, '-');
					toc.push({ level, text: raw, id });
				}
				return toc;
			}
			return post.content.split('\n').map(line => {
				let level = 0; let text = '';
				if (line.startsWith('### ')) { level = 3; text = line.slice(4); }
				else if (line.startsWith('## ')) { level = 2; text = line.slice(3); }
				else if (line.startsWith('# ')) { level = 1; text = line.slice(2); }
				if (!level) return null;
				const id = text.toLowerCase().replace(/[^a-z0-9\s-]/g, '').trim().replace(/\s+/g, '-');
				return { level, text, id };
			}).filter(Boolean);
	}, [post]);

	const renderContentHtml = useMemo(() => {
			if (!post?.content) return '';
			const isHtml = /<\/?[a-z][\s\S]*>/i.test(post.content);
			if (isHtml) {
				// Inject IDs onto h1-h3 for TOC linking
				return post.content.replace(/<h([1-3])([^>]*)>([\s\s]*?)<\/h\1>/gi, (m, lvl, attrs, inner) => {
					const text = inner.replace(/<[^>]+>/g, '');
					const id = text.toLowerCase().replace(/[^a-z0-9\s-]/g,'').trim().replace(/\s+/g,'-');
					// Remove any existing id attributes
					const attrsNoId = String(attrs || '').replace(/\s+id="[^"]*"/i, '');
					return `<h${lvl} id="${id}"${attrsNoId}>${inner}</h${lvl}>`;
				});
			}
			const lines = post.content.split('\n');
			let html = ''; let inList = false; let inCode = false;

			const closeList = () => { if (inList) { html += '</ul>'; inList = false; } };
			const esc = (s) => s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');

			for (let raw of lines) {
				const line = raw.trimEnd();
				if (line.startsWith('```')) { if (!inCode) { closeList(); html += '<pre><code>'; inCode = true; } else { html += '</code></pre>'; inCode = false; } continue; }
				if (inCode) { html += `${esc(raw)}\n`; continue; }
				if (line.startsWith('![') && line.includes('](')) {
					closeList();
				const m = line.match(/!\[(.*?)\]\((.*?)(?:\s+"(.*?)")?\)/);
					if (m) { const [,alt,url,cap] = m; html += cap ? `<figure><img src="${url}" alt="${alt}" /><figcaption>${cap}</figcaption></figure>` : `<img src="${url}" alt="${alt}" />`; }
					continue;
				}
				const head = (lvl, text) => { const id = text.toLowerCase().replace(/[^a-z0-9\s-]/g,'').trim().replace(/\s+/g,'-'); return `<h${lvl} id="${id}">${text}</h${lvl}>`; };
				if (line.startsWith('# ')) { closeList(); html += head(1, line.slice(2)); continue; }
				if (line.startsWith('## ')) { closeList(); html += head(2, line.slice(3)); continue; }
				if (line.startsWith('### ')) { closeList(); html += head(3, line.slice(4)); continue; }
				if (line.startsWith('> ')) { closeList(); html += `<blockquote>${line.slice(2)}</blockquote>`; continue; }
				if (line.startsWith('- ')) { if (!inList) { html += '<ul>'; inList = true; } html += `<li>${line.slice(2)}</li>`; continue; }
				if (line.trim() === '') { closeList(); continue; }
				closeList(); html += `<p>${line}</p>`;
			}
			closeList(); if (inCode) html += '</code></pre>';
			return html;
	}, [post]);

	// Redirect if blog is disabled (after all hooks)
	if (settings?.features?.blogEnabled === false) {
		return <Navigate to="/" replace />;
	}

	if (loading) return <LoadingSpinner fullScreen text="Loading article..." />;
	if (!post) return <Navigate to="/blog" replace />;

	const formatDate = (s) => new Date(s).toLocaleDateString('en-US', { year:'numeric', month:'long', day:'numeric' });
	const initials = (name='S H') => name.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase();

	return (
		<Page>
			<Progress aria-hidden>
				<ProgressFill $w={progress} />
			</Progress>
			<Container>
				<Back to="/blog"><FiArrowLeft /> Back to Blog</Back>
				<Grid>
					<Article>
						<Title>{post.title}</Title>
						<Meta>
							<span className="pill"><FiCalendar /> {formatDate(post.date)}</span>
							<span className="pill"><FiClock /> {post.readTime}</span>
							<span className="pill category">{post.category}</span>
						</Meta>
						<AuthorRow>
							<div className="avatar">{initials(post.author?.name || 'S H')}</div>
							<div className="info">
								<div className="name">{post.author?.name}</div>
								{post.author?.bio && <div className="bio">{post.author.bio}</div>}
							</div>
						</AuthorRow>
						{post.image && (<FeaturedImage src={post.image} alt={post.title} />)}
						<Content dangerouslySetInnerHTML={{ __html: renderContentHtml }} />
						{Array.isArray(post.tags) && post.tags.length > 0 && (
							<Tags>
								{post.tags.map((t, i) => (<span className="tag" key={i}>{t}</span>))}
							</Tags>
						)}
					</Article>
					<Aside>
						{toc.length > 0 && (
							<Card>
								<h3 style={{marginTop:0}}>On this page</h3>
								<Toc>
									{toc.map((i, idx) => (
										<li key={idx} className={`lvl-${i.level}`}><a href={`#${i.id}`}>{i.text}</a></li>
									))}
								</Toc>
							</Card>
						)}
						<Card>
							<h3 style={{marginTop:0, display:'flex', justifyContent:'space-between', alignItems:'center'}}>
								<span>Featured Courses</span>
								<Link to="/courses" style={{fontSize:'0.875rem', color: '#ef4444', textDecoration:'none'}}>View All</Link>
							</h3>
							{featuredCourses.map((c) => (
								<CourseMini key={c.id || c._id} to={`/courses/${c.slug}`}>
									<div className="title">{c.title}</div>
									<div className="meta">
										<span><FiUsers /> {c.students}</span>
										<span><FiStar /> {c.rating}</span>
									</div>
								</CourseMini>
							))}
						</Card>
						<Card>
							<h3 style={{marginTop:0}}>Start Learning Today</h3>
							<p style={{color:'#6b7280'}}>Explore our comprehensive courses and take your tech career to the next level.</p>
							<Button as={Link} to="/courses" style={{width:'100%'}}>Browse All Courses</Button>
						</Card>
					</Aside>
				</Grid>
			</Container>
		</Page>
	);
}
